import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Message,
  RadioGroup,
  toast,
} from '@workwhile/ui'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ModalWithSelect } from 'components/ModalSelect'
import { InvoiceConfigValuesFields } from '../../../components/InvoiceConfigValuesFields'
import { OwnerPicker } from '../../../components/OwnerPicker'
import {
  createInvoiceConfigFlowSchema,
  CreateInvoiceConfigFlowSchema,
  toInvoiceConfigCustomWeek,
  toInvoiceConfigOwnerIds,
} from '../../../schemas/invoiceConfigValues'
import { useInvoiceConfigWorkflowsContextValue } from '../../../context/InvoiceConfigWorkflowsProvider'
import { useCreateInvoiceConfigFlowContextValue } from '../context/CreateInvoiceConfigFlowProvider'

const defaultValues: CreateInvoiceConfigFlowSchema = {
  ownerType: 'company',
  companyId: undefined,
  companyGroupId: undefined,
  breakdownLevels: [],
  useCustomWeek: false,
  weekStartDay: undefined,
  timezone: undefined,
}

export const CreateInvoiceConfigFlowMain = () => {
  const {
    createInvoiceConfigFlow: { isOpen, close },
  } = useInvoiceConfigWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateInvoiceConfigFlowContextValue()

  const methods = useForm<CreateInvoiceConfigFlowSchema>({
    resolver: zodResolver(createInvoiceConfigFlowSchema),
    defaultValues,
  })

  const { control, handleSubmit, reset, setValue } = methods

  const ownerType = useWatch({ control, name: 'ownerType' })

  useEffect(() => {
    if (!isOpen) return
    reset(defaultValues)
  }, [isOpen, reset])

  const handleClose = () => {
    reset(defaultValues)
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        ...toInvoiceConfigOwnerIds(data),
        breakdownLevels: data.breakdownLevels,
        customWeek: toInvoiceConfigCustomWeek(data),
      },
      {
        onSuccess: () => {
          toast.success('Invoice config created successfully', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Create
          </Button>
        </Flex>
      }
    >
      <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
        Create invoice config
      </Heading>
      <FormProvider {...methods}>
        <Box
          as="form"
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
          style={{ width: '100%' }}
        >
          <Box mt={4}>
            <Controller
              control={control}
              name="ownerType"
              render={({ field }) => (
                <Field label="Owner">
                  <RadioGroup
                    name={field.name}
                    value={field.value}
                    onChange={(next) => {
                      const ownerTypeValue = next as 'company' | 'companyGroup'
                      field.onChange(ownerTypeValue)
                      if (ownerTypeValue === 'company') {
                        setValue('companyGroupId', undefined)
                      } else {
                        setValue('companyId', undefined)
                      }
                    }}
                    // RadioGroup flexDirection prop is valid at runtime but not inferred
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error - valid prop but type not inferred correctly
                    flexDirection={['column', 'row']}
                    gap={5}
                    options={[
                      { label: 'Company', value: 'company' },
                      { label: 'Company Group', value: 'companyGroup' },
                    ]}
                  />
                </Field>
              )}
            />
          </Box>
          <Box mt={4}>
            {ownerType === 'company' ? (
              <Controller
                control={control}
                name="companyId"
                render={({ field, fieldState }) => (
                  <OwnerPicker
                    ownerType="company"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message?.toString()}
                  />
                )}
              />
            ) : (
              <Controller
                control={control}
                name="companyGroupId"
                render={({ field, fieldState }) => (
                  <OwnerPicker
                    ownerType="companyGroup"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message?.toString()}
                  />
                )}
              />
            )}
          </Box>
          <InvoiceConfigValuesFields />
          {errorMessage ? (
            <Message variant="error" mt={3} borderRadius="small">
              {errorMessage}
            </Message>
          ) : null}
        </Box>
      </FormProvider>
    </ModalWithSelect>
  )
}
